import moment from "moment";
import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import { Complaintmodel } from "../../../../service/models/complaint_model";
import {
  CreateComplaint,
  GetComplaintAttachmentSignedUrlApi,
  GetComplaintCreationDetailsByMobile,
  UpdateComplaint,
  UploadComplaintAttachmentToS3Api,
} from "../../../../service/repos/support/complaint_repo";
import { DeviceIdRegex } from "../../../../utils/constant";
import {
  ACTION,
  complaint_by,
  complaintStatus,
  homeInstallationComplaintStatus,
  MODULE,
  PriorityType,
  SubDepartments,
  ComplaintTicketType,
  SourceType,
  ComplaintsRelatedToWithSubCategory,
  ComplaintsRelatedToWithSubCategory_HPCL,
  HomeInstallTicketType,
  COMPLAINT_ATTACHMENT_ACCEPT,
  COMPLAINT_ATTACHMENT_LIMITS,
  COMPLAINT_ATTACHMENT_TOTAL_MB,
} from "../../../../utils/enums";
import {
  checkCompressedSize,
  formatSizeMB,
  getAttachmentCategory,
} from "../../../../utils/attachment_validator";
import {
  compressVideo,
  CompressionStage,
  shouldCompressVideo,
} from "../../../../utils/video_compressor";
import { compressImage } from "../../../../utils/image_compressor";
import ImageTile from "../../../components/common/image_tile";
import PdfTile from "../../../components/common/pdf_tile";
import LoadingWidget from "../../../components/loading_spinner";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../new_components/common/drawer_modal";
import FileDropZone from "../../../new_components/common/file_dropdown";
import ModalDialog from "../../../new_components/common/modal";
import EmployeeSearchWidget from "../../../new_components/emp_search_widget";
import OrgSearchField from "../listing/components/organizationSeach";
import AttachmentWidget from "../detail/components/attaments_widget";
import CommentWidget from "./components/comment_widget";
import LogsWidget from "./components/logs_widget";
import {
  ModalAlignment,
  ModalType,
  useModal,
} from "../../../../context_providers/modal/modal_context";
import { AddressModel } from "../../../../service/models/address_model";
import AddressPickerWidget from "../../../new_components/common/location/AddressPicker";
import ComplaintRelatedToSearchField from "./components/complaints_related_typeable_widget";
import OrderSearchField from "../shared/order_search_field";
import RequiredMark from "../../../new_components/common/required_mark";
import {
  getLockedFieldsFromLookup,
  HomePlatformLookupResult,
  isValidPhoneNumber,
  LockableField,
  mergeComplaintWithLookup,
  normaliseHomePlatformLookup,
  parseDeviceTokens,
  sanitizePhoneNumber,
} from "../shared/home_platform_lookup";
import DeviceTabs, { DeviceVerifyResult } from "../shared/device_tabs";

interface CreateComplaintPageProps {
  location: any;
}

const defaultCreateComplaints = (): Complaintmodel => {
  return {
    org: "",
    complainant_name: "",
    complainant_mobile: "",
    reporter_name: "",
    reporter_mobile: "",
    complaint_by: "",
    related_to: "",
    subcategory_of_related_to: "",
    category: "",
    subject: "",
    description: "",
    pre_post_delivery: "",
    res_dep: "",
    res_person: "",
    res_person_name: "",
    res_person_sec_dept: "",
    status: complaintStatus.open,
    ticket_type: ComplaintTicketType.incident,
    source: SourceType.call,
    source_platform: "ERP_Platform",
    state: "",
    pincode: "",
    next_action_date: Date.now(),
    complaint_date: Date.now(),
    active: true,
    priority: PriorityType.low,
    createdByDept: "",
    device_working_status: "",
    severity: PriorityType.medium,
    safety_flag: false,
    rca_required: false,
    home_sync_status: "",
    home_sync_failure_reason: "",
  };
};

const SectionCard: FunctionComponent<{
  title: string;
  subtitle?: string;
  children: ReactNode;
}> = ({ title, subtitle, children }) => (
  <div className="border rounded-md bg-white p-3 sm:p-5 space-y-3 sm:space-y-4">
    <div>
      <div className="text-sm font-semibold">{title}</div>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
    <div className="space-y-3 sm:space-y-4">{children}</div>
  </div>
);

const CreateComplaintPage: FunctionComponent<CreateComplaintPageProps> = (
  props,
) => {
  const [disabled, setDisabled] = useState(true);
  const { showToast } = useToast();
  const { showModal, onClose } = useModal();
  const { user, isAuthorised } = useAuth();
  const query = new URLSearchParams(props.location.search);
  const id = query.get("id");

  const [complaint, setComplaint] = useState<Complaintmodel>({
    ...defaultCreateComplaints(),
  });

  const [edit, setEdit] = useState(id ? false : true);
  const [showAttachments, setShowAttachments] = useState(false);
  const [files, setFile] = useState<File[]>([]);
  const [videoProgress, setVideoProgress] = useState<{
    [name: string]: number;
  }>({});
  // Videos are uploaded when picked, not on submit, so the complaint payload
  // carries their URLs instead of their bytes. Keyed by the File itself, since
  // two picked videos can share a name and would overwrite each other.
  const [uploadedVideos, setUploadedVideos] = useState<Map<File, string>>(
    new Map(),
  );
  const [videoCompression, setVideoCompression] = useState<{
    name: string;
    stage: CompressionStage;
    percent: number;
    cancel: () => void;
  }>();
  const [loading, setLoading] = useState(false);
  const [mobileLookupLoading, setMobileLookupLoading] = useState(false);
  const [showLogs, setShowLogs] = useState<boolean>(false);
  const [showComments, setShowComments] = useState<boolean>(false);
  const [lookupResult, setLookupResult] = useState<HomePlatformLookupResult>();
  const [selectedLookupDeviceId, setSelectedLookupDeviceId] = useState("");
  const [lockedFields, setLockedFields] = useState<Set<LockableField>>(
    new Set(),
  );
  const [lookupMobile, setLookupMobile] = useState("");
  const [lookupIdentifier, setLookupIdentifier] = useState("");
  const [homeAppNoInstallation, setHomeAppNoInstallation] = useState(false);
  // A failed/not-found lookup still leaves lookupResult undefined, so it alone
  // can't tell "never searched" apart from "searched, found nothing" — which
  // let auxiliary fields (Reporter phone, Complainant Contact) re-run the same
  // lookup on every blur, each time wiping org/customer fields already typed.
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const source = params.get("source") ?? "";
  const incident_id = params.get("incident_id") ?? "";
  const complainant_number = params.get("complainant_number") ?? "";
  const complainant_name = params.get("complainant_name") ?? "";
  const complainant_org = params.get("complainant_org") ?? "";
  const complaint_related_to = params.get("complaint_related_to") ?? "";
  const subcategory_of_related_to = params.get("subcategory_of_related_to");
  const isHPCL = complaint.org?.toLowerCase().trim() === "hpcl";
  const isHelpDeskTicket =
    complaint.ticket_type === ComplaintTicketType.help_desk;

  const complaintMapping = isHPCL
    ? ComplaintsRelatedToWithSubCategory_HPCL
    : ComplaintsRelatedToWithSubCategory;

  const isHomeInstallation = !!complaint.res_dep?.includes("Home Installation");
  // Every Operations sub-department (res_dep "Operations-*") syncs to the Home
  // App, which supports only Incident/Service Request — so restrict the ticket
  // type to those two for all of Operations, not just Home Installation.
  const isOperations = !!complaint.res_dep?.startsWith("Operations");

  const complaint_categories = Object.keys(complaintMapping);

  const allowEmptySubCategory = ["Multiverse"];

  const subCategories = (
    complaint.related_to && complaintMapping[complaint.related_to]
      ? complaintMapping[complaint.related_to]
      : []
  )
    .slice()
    .sort();

  const isSubCategoryRequired: boolean =
    !!complaint.related_to &&
    !allowEmptySubCategory.includes(complaint.related_to);

  const selectedDevice = lookupResult?.devices?.find(
    (d) => d.id === selectedLookupDeviceId,
  );

  const selectedRecord = selectedDevice?.raw;
  const deviceTokens = parseDeviceTokens(complaint.device_id);

  // Per-device verification reusing the existing Home Platform lookup.
  const verifyDeviceForTabs = async (
    deviceId: string,
  ): Promise<DeviceVerifyResult> => {
    const res = await GetComplaintCreationDetailsByMobile(deviceId);
    if (!res?.success) {
      const raw = String(res?.error ?? "");
      const reason =
        raw && !raw.toLowerCase().includes("installation")
          ? raw
          : "Device not found on Multiverse";
      return { verified: false, reason };
    }
    const lookup = normaliseHomePlatformLookup(res.data);
    const matchedDevice =
      lookup.devices.find((d) => d.id.toLowerCase() === deviceId) ??
      lookup.devices[0];
    const record = matchedDevice?.raw ?? lookup.raw?.[0];
    const found =
      lookup.devices.length > 0 ||
      (res.data && Object.keys(res.data).length > 0);
    if (!found) {
      return {
        verified: false,
        reason:
          "Device ID not found in Multiverse. Please check and try again.",
      };
    }
    return {
      verified: true,
      details: {
        product_name: record?.product_name ?? record?.productName,
        firmware_version: record?.firmware_version ?? record?.firmwareVersion,
        hardware_version: record?.hardware_version ?? record?.hardwareVersion,
        batch_number: record?.batch_number ?? record?.batchNumber,
        mfg_date: record?.mfg_date ?? record?.mfgDate,
        wcr_present: matchedDevice?.wcrPresent,
      },
    };
  };

  const handleDeviceTabsChange = (next: {
    devices: string[];
    deviceMeta: typeof complaint.device_meta;
  }) => {
    setComplaint((c) => ({
      ...c,
      device_id: next.devices.length ? next.devices.join(",") : "",
      device_meta: next.devices.length ? next.deviceMeta : undefined,
    }));
  };

  const detailsFromLookupRecord = (raw: any) => {
    const out: Record<string, any> = {};
    const map: Record<string, string[]> = {
      product_name: ["product_name", "productName"],
      firmware_version: ["firmware_version", "firmwareVersion"],
      hardware_version: ["hardware_version", "hardwareVersion"],
      batch_number: ["batch_number", "batchNumber"],
      mfg_date: ["mfg_date", "mfgDate"],
      installation_date: ["installationDate", "installation_date", "createdAt"],
    };
    for (const [key, aliases] of Object.entries(map)) {
      for (const a of aliases) {
        if (raw?.[a] !== undefined && raw?.[a] !== null && raw?.[a] !== "") {
          out[key] = raw[a];
          break;
        }
      }
    }
    return out;
  };

  const addVerifiedDevices = (
    current: Complaintmodel,
    devices: { id: string; raw?: any; wcrPresent?: boolean }[],
  ): { device_id: string; device_meta: Complaintmodel["device_meta"] } => {
    const ids = parseDeviceTokens(current.device_id);
    const meta: any = { ...(current.device_meta ?? {}) };
    for (const d of devices) {
      const id = (d?.id ?? "").trim().toLowerCase();
      if (!id) continue;
      if (!ids.includes(id)) ids.push(id);
      meta[id] = {
        ...(meta[id] ?? {}),
        verified: true,
        ...detailsFromLookupRecord(d.raw),
        wcr_present: d?.wcrPresent,
      };
    }
    return {
      device_id: ids.length ? ids.join(",") : "",
      device_meta: ids.length ? meta : undefined,
    };
  };

  useEffect(() => {
    // Every Operations sub-department (including Home Installation) defaults to
    // Post Delivery; switching department clears the type/category selections.
    setComplaint((c) => ({
      ...c,
      ticket_type: "",
      related_to: "",
      category: "",
      subcategory_of_related_to: "",
      pre_post_delivery: isOperations ? "Post Delivery" : "",
    }));
  }, [complaint.res_dep]);

  const handleReporterMobileChange = (value: string) => {
    const sanitized = sanitizePhoneNumber(value);
    setComplaint((c) => ({
      ...c,
      reporter_mobile: sanitized,
    }));
  };

  const fetchMultipleDeviceIds = async (ids: string[]) => {
    setLookupResult(undefined);
    setSelectedLookupDeviceId("");
    setLookupMobile("");
    setHomeAppNoInstallation(false);
    setMobileLookupLoading(true);

    const verifiedIds: string[] = [];
    const failed: { id: string; reason: string }[] = [];
    const verifiedDevices: { id: string; raw?: any; wcrPresent?: boolean }[] =
      [];
    const failedMeta: Record<string, any> = {};
    let firstLookup: HomePlatformLookupResult | undefined;
    let firstVerifiedId = "";

    try {
      for (const id of ids) {
        const res = await GetComplaintCreationDetailsByMobile(id);
        const reasonFromRes = (() => {
          const raw = String(res?.error ?? "");
          return raw && !raw.toLowerCase().includes("installation")
            ? raw
            : "Device not found on Home Installation";
        })();

        if (!res?.success) {
          failed.push({ id, reason: reasonFromRes });
          failedMeta[id] = {
            verified: false,
            verify_failed_reason: reasonFromRes,
          };
          continue;
        }
        const lookup = normaliseHomePlatformLookup(res.data);
        const matchedDevice =
          lookup.devices.find((d) => d.id.toLowerCase() === id) ??
          lookup.devices[0];
        const record = matchedDevice?.raw ?? lookup.raw?.[0];
        const found =
          lookup.devices.length > 0 ||
          (res.data && Object.keys(res.data).length > 0);
        if (!found) {
          const reason = "Device ID not found in Multiverse";
          failed.push({ id, reason });
          failedMeta[id] = { verified: false, verify_failed_reason: reason };
          continue;
        }
        verifiedIds.push(id);
        verifiedDevices.push({
          id,
          raw: record,
          wcrPresent: matchedDevice?.wcrPresent,
        });
        if (!firstLookup) {
          firstLookup = lookup;
          firstVerifiedId = id;
        }
      }

      if (firstLookup) {
        setLockedFields((prev) => {
          const next = new Set(prev);
          getLockedFieldsFromLookup(firstLookup, firstVerifiedId).forEach(
            (field) => next.add(field),
          );
          return next;
        });
      }

      setComplaint((current) => {
        // First-verified device wins for customer/org/address prefill.
        let next = firstLookup
          ? mergeComplaintWithLookup(current, firstLookup, firstVerifiedId)
          : current;
        next = { ...next, ...addVerifiedDevices(next, verifiedDevices) };
        // Keep failed devices too (flagged), so the agent sees them in tabs.
        const meta: any = { ...(next.device_meta ?? {}) };
        const tokenIds = parseDeviceTokens(next.device_id);
        for (const [fid, fmeta] of Object.entries(failedMeta)) {
          if (!tokenIds.includes(fid)) tokenIds.push(fid);
          meta[fid] = { ...(meta[fid] ?? {}), ...fmeta };
        }
        return {
          ...next,
          device_id: tokenIds.length ? tokenIds.join(",") : "",
          device_meta: tokenIds.length ? meta : undefined,
        };
      });

      const parts: string[] = [];
      if (verifiedIds.length) parts.push(`Verified: ${verifiedIds.join(", ")}`);
      if (failed.length)
        parts.push(`Failed: ${failed.map((f) => f.id).join(", ")}`);
      showToast({
        type: failed.length ? ToastType.warning : ToastType.success,
        text: parts.join("  ·  ") || "No devices processed",
      });
    } finally {
      setMobileLookupLoading(false);
    }
  };

  const fetchComplaintContextByMobile = async (
    mobile?: string,
    { resetFields = true }: { resetFields?: boolean } = {},
  ) => {
    const rawIdentifier = String(mobile ?? lookupIdentifier ?? "").trim();
    const deviceIdCandidate = rawIdentifier.toLowerCase();
    const phoneCandidate = sanitizePhoneNumber(rawIdentifier);

    const isPhoneLookup = isValidPhoneNumber(phoneCandidate);
    const isDeviceLookup = DeviceIdRegex.test(deviceIdCandidate);

    // Multiple device IDs (comma/space separated) → verify each and add all.
    const multiTokens = rawIdentifier
      .split(/[\s,]+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    if (
      !isPhoneLookup &&
      multiTokens.length > 1 &&
      multiTokens.every((t) => DeviceIdRegex.test(t))
    ) {
      await fetchMultipleDeviceIds(Array.from(new Set(multiTokens)));
      return;
    }

    if (!isPhoneLookup && !isDeviceLookup) {
      showToast({
        type: ToastType.error,
        text: "Please enter a valid 10 digit mobile number or one/more 6 digit device ids",
      });
      return;
    }

    setLookupAttempted(true);

    if (resetFields) {
      setLookupResult(undefined);
      setSelectedLookupDeviceId("");
      setLookupMobile("");
      setHomeAppNoInstallation(false);
      setLockedFields(new Set());

      if (isPhoneLookup) {
        setComplaint((c) => ({
          ...c,
          org: "",
          complainant_name: "",
          complainant_mobile: phoneCandidate,
          address: undefined,
          state: "",
          city: "",
          pincode: "",
          dealer_id: "",
        }));
      }
    }

    setMobileLookupLoading(true);

    try {
      const response = await GetComplaintCreationDetailsByMobile(
        isPhoneLookup ? phoneCandidate : deviceIdCandidate,
      );
      console.log("fetchComplaintContextByMobile response", response);

      if (!response?.success) {
        setLookupResult(undefined);
        setSelectedLookupDeviceId("");
        setLookupMobile("");
        const rawMsg = response?.error ?? "";
        const parsedMsg = (() => {
          try {
            const jsonMatch = rawMsg.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              if (parsed.message) return String(parsed.message);
            }
          } catch {}
          return rawMsg;
        })();
        const isNotFound =
          !parsedMsg ||
          parsedMsg.toLowerCase().includes("not found") ||
          parsedMsg.toLowerCase().includes("installation");
        if (isDeviceLookup && isNotFound) {
          setHomeAppNoInstallation(true);
        }
        showToast({
          type:
            isDeviceLookup || isNotFound ? ToastType.warning : ToastType.error,
          text: isDeviceLookup
            ? "Device not found on Home Installation. Please fill details manually."
            : parsedMsg || "Failed to fetch home platform details",
        });
        return;
      }

      if (
        isDeviceLookup &&
        (response.oemInstallationNotFound || response.source === "multiverse")
      ) {
        setHomeAppNoInstallation(true);
      }

      const lookup = normaliseHomePlatformLookup(response.data);

      if (!lookup || (!lookup.devices.length && !lookup.customerName)) {
        if (isDeviceLookup) setHomeAppNoInstallation(true);
        setLookupResult(undefined);
        setSelectedLookupDeviceId("");
        setLookupMobile("");
        if (isDeviceLookup && deviceIdCandidate) {
          setComplaint((current) => {
            const ids = parseDeviceTokens(current.device_id);
            if (!ids.includes(deviceIdCandidate)) ids.push(deviceIdCandidate);
            return {
              ...current,
              device_id: ids.join(","),
              device_meta: {
                ...(current.device_meta ?? {}),
                [deviceIdCandidate]: {
                  ...(current.device_meta?.[deviceIdCandidate] ?? {}),
                  verified: false,
                  verify_failed_reason: "Device not found on Home Installation",
                },
              },
            };
          });
        }
        showToast({
          type: ToastType.warning,
          text: isDeviceLookup
            ? "Device not found on Home Installation. Please fill details manually."
            : "Could not find linked home platform devices for this entry. Complete the form manually if required.",
        });
        return;
      }

      // Phone lookup with no device — fill customer details directly without device selection
      if (isPhoneLookup && !lookup.devices.length && lookup.customerName) {
        const address = lookup.customerAddress
          ? {
              line1: lookup.customerAddress,
              city: lookup.customerCity ?? "",
              state: lookup.customerState ?? "",
              country: "India",
              pincode: Number(lookup.customerPincode) || 0,
              full_address: lookup.customerAddress,
            }
          : undefined;
        const rawDeviceId = lookup.raw?.[0]?.deviceId || "";
        const noDeviceLocked = getLockedFieldsFromLookup(lookup);
        noDeviceLocked.add("complainant_mobile");
        setLockedFields((prev) => {
          const next = new Set(prev);
          noDeviceLocked.forEach((field) => next.add(field));
          return next;
        });
        setComplaint((current) => ({
          ...current,
          complainant_name:
            current.complainant_name || lookup.customerName || "",
          complainant_mobile: phoneCandidate,
          reporter_name: current.reporter_name || lookup.customerName || "",
          org: lookup.orgName || current.org,
          state: lookup.customerState || current.state,
          city: lookup.customerCity || current.city,
          pincode: lookup.customerPincode || current.pincode,
          ...(rawDeviceId
            ? {
                device_id: rawDeviceId,
                device_meta: {
                  ...(current.device_meta ?? {}),
                  [rawDeviceId.toLowerCase()]: {
                    ...(current.device_meta?.[rawDeviceId.toLowerCase()] ?? {}),
                    verified: true,
                    ...detailsFromLookupRecord(lookup.raw?.[0]),
                  },
                },
              }
            : {}),
          ...(address ? { address } : {}),
        }));
        setLookupMobile(phoneCandidate);
        setLookupIdentifier(phoneCandidate);
        showToast({
          type: ToastType.success,
          text: "Details fetched from Home",
        });
        return;
      }

      setLookupResult(lookup);
      console.log("devices : ", lookup.devices);
      const normalisedIdentifier = isPhoneLookup
        ? phoneCandidate
        : deviceIdCandidate;
      setLookupMobile(normalisedIdentifier);
      setLookupIdentifier(normalisedIdentifier);

      const preferredDeviceId = isDeviceLookup ? deviceIdCandidate : "";
      const firstDeviceId =
        lookup.devices.find((d) => d.id === preferredDeviceId)?.id ??
        lookup.devices[0]?.id ??
        "";
      setSelectedLookupDeviceId(firstDeviceId);
      setLockedFields((prev) => {
        const next = new Set(prev);
        getLockedFieldsFromLookup(lookup, firstDeviceId).forEach((field) =>
          next.add(field),
        );
        if (isPhoneLookup) next.add("complainant_mobile");
        return next;
      });
      setComplaint((current) => {
        const merged = mergeComplaintWithLookup(
          {
            ...current,
            ...(isPhoneLookup ? { complainant_mobile: phoneCandidate } : {}),
          },
          lookup,
          firstDeviceId,
        );

        return { ...merged, ...addVerifiedDevices(merged, lookup.devices) };
      });

      showToast({
        type: ToastType.success,
        text:
          response.source === "multiverse"
            ? "Details fetched from Multiverse"
            : "Details fetched from Home",
      });
    } finally {
      setMobileLookupLoading(false);
    }
  };

  /**
   * Compresses a picked video before it joins the attachment list, so what the
   * user sees in the preview is exactly what gets uploaded. Returns undefined
   * when the video cannot be used at all.
   */
  const prepareVideo = async (file: File) => {
    // Within the cap: upload as-is, so no compressor download and no waiting.
    if (!shouldCompressVideo(file)) return file;

    const controller = new AbortController();
    setVideoCompression({
      name: file.name,
      stage: "loading",
      percent: 0,
      cancel: () => controller.abort(),
    });

    try {
      const result = await compressVideo(file, {
        signal: controller.signal,
        onProgress: ({ stage, percent }) =>
          setVideoCompression((prev) =>
            prev ? { ...prev, stage, percent: Math.round(percent) } : prev,
          ),
      });

      const tooLarge = checkCompressedSize(result.file);
      if (tooLarge) {
        showToast({
          type: ToastType.error,
          text: result.compressed
            ? tooLarge
            : `Could not compress "${file.name}" (${
                result.fallbackReason ?? "compression failed"
              }). ${tooLarge}`,
        });
        return undefined;
      }

      if (result.compressed) {
        showToast({
          type: ToastType.success,
          text: `"${file.name}" compressed from ${formatSizeMB(
            result.originalSize,
          )} to ${formatSizeMB(result.file.size)}.`,
        });
      }

      return result.file;
    } finally {
      setVideoCompression(undefined);
    }
  };

  /**
   * Uploads a prepared video and returns its URL, which submit then sends with
   * the complaint. Uploading here rather than after creation means a failure
   * can be retried while the form is still open, instead of leaving a created
   * complaint with a missing attachment.
   */
  const uploadVideo = async (video: File) => {
    setVideoProgress((prev) => ({ ...prev, [video.name]: 0 }));

    const fail = (error?: string) => {
      showToast({
        type: ToastType.error,
        text: `Could not upload "${video.name}". ${
          error ?? "Please remove it and try again."
        }`,
      });
      return undefined;
    };

    try {
      const extension = video.name.split(".").pop() ?? "mp4";
      const signed = await GetComplaintAttachmentSignedUrlApi({
        mimetype: video.type,
        extension,
      });
      if (!signed.success) return fail(signed.error);

      const { signed_url, url } = signed.data;
      const uploaded = await UploadComplaintAttachmentToS3Api({
        signed_url,
        file: video,
        onProgress: (percent) =>
          setVideoProgress((prev) => ({ ...prev, [video.name]: percent })),
      });
      if (!uploaded.success) return fail(uploaded.error);

      setUploadedVideos((prev) => new Map(prev).set(video, url));
      return url;
    } finally {
      setVideoProgress((prev) => {
        const next = { ...prev };
        delete next[video.name];
        return next;
      });
    }
  };

  const submit = async (e: any) => {
    e.preventDefault();

    if (Object.keys(videoProgress).length > 0) {
      showToast({
        type: ToastType.info,
        text: "A video is still uploading. Please wait for it to finish.",
      });
      return;
    }

    const phone = sanitizePhoneNumber(complaint.complainant_mobile ?? "");
    if (!isValidPhoneNumber(phone)) {
      showToast({
        type: ToastType.error,
        text: "Please enter a valid complainant contact number",
      });
      return;
    }

    if (!complaint.subject?.trim()) {
      showToast({
        type: ToastType.error,
        text: "Please enter subject",
      });
      return;
    }

    const isHPCL = complaint.org?.toLowerCase().trim() === "hpcl";
    const isHelpDesk = complaint.ticket_type === ComplaintTicketType.help_desk;
    const isStrict = !isHPCL && !isHelpDesk;

    if (isStrict) {
      if (!complaint.complainant_name?.trim()) {
        showToast({
          type: ToastType.error,
          text: "Please enter complainant name",
        });
        return;
      }

      if (!complaint.related_to?.trim()) {
        showToast({
          type: ToastType.error,
          text: "Please select complaint category",
        });
        return;
      }

      if (deviceTokens.length) {
        const missing = deviceTokens.filter(
          (d) => !complaint.device_meta?.[d]?.working_status,
        );
        if (missing.length) {
          showToast({
            type: ToastType.error,
            text: `Please select working status for device(s): ${missing.join(", ")}`,
          });
          return;
        }
      } else if (!complaint.device_working_status) {
        showToast({
          type: ToastType.error,
          text: "Please select device working status",
        });
        return;
      }

      // if (!complaint.pincode?.trim()) {
      //   showToast({
      //     type: ToastType.error,
      //     text: "Please enter pincode",
      //   });
      //   return;
      // }
    }

    // if (complaint.source === "home_platform" && !complaint.device_id?.trim()) {
    //   showToast({
    //     type: ToastType.error,
    //     text: "Device ID is required for home platform",
    //   });
    //   return;
    // }

    // Looked up by phone rather than device ID — device ID isn't required
    // in that case since the customer/installation was already resolved.
    const lookedUpByPhone = isValidPhoneNumber(lookupMobile);

    if (
      complaint.res_dep?.includes("Home Installation") &&
      !lookedUpByPhone &&
      !complaint.device_id?.trim()
    ) {
      showToast({
        type: ToastType.error,
        text: "Device ID is required for Home Installation complaints",
      });
      return;
    }

    if (
      complaint.complaint_by === "Host" &&
      !complaint.order_id &&
      complaint.org?.toLowerCase() !== "hpcl" &&
      complaint?.created_by !== "Kazam_App"
    ) {
      showToast({
        type: ToastType.error,
        text: "Order ID is required for host complaints",
      });
      return;
    }

    const complaintDate = new Date(complaint.complaint_date).getTime();
    const nextDate = new Date(complaint.next_action_date).getTime();
    if (nextDate < complaintDate) {
      showToast({
        type: ToastType.error,
        text: "Due date cannot be before complaint date",
      });
      return;
    }

    let ids = complaint.device_id ? complaint.device_id.split(",") : [];
    const validator = DeviceIdRegex;
    for (let i = 0; i < ids.length; i++) {
      let id = ids[i].trim().toLowerCase();

      if (!validator.test(id)) {
        showToast({
          type: ToastType.warning,
          text: `device id : ${id} is not valid`,
        });
        return;
      }
      ids[i] = id;
    }

    complaint.device_id = ids.length ? ids.join(",") : undefined;
    complaint.complainant_mobile = phone;

    if (ids.length) {
      const firstStatus = complaint.device_meta?.[ids[0]]?.working_status;
      complaint.device_working_status =
        firstStatus || complaint.device_working_status || "Not Working";

      // Keep device_meta keys ⊆ device_id (backend refine rejects stray keys).
      if (complaint.device_meta) {
        const pruned: NonNullable<typeof complaint.device_meta> = {};
        ids.forEach((d) => {
          if (complaint.device_meta?.[d]) pruned[d] = complaint.device_meta[d];
        });
        complaint.device_meta = pruned;
      }
    } else {
      complaint.device_meta = undefined;
    }

    setLoading(true);
    let form = new FormData();

    // Videos were uploaded when they were picked, so only their URLs travel with
    // the complaint and the request body stays small.
    const videos = files.filter((f) => getAttachmentCategory(f) === "video");
    const inline_files = files.filter(
      (f) => getAttachmentCategory(f) !== "video",
    );

    const attachments = videos.map((f) => ({
      file_name: f.name,
      file_url: uploadedVideos.get(f),
      size: f.size,
      uploaded_at: Date.now(),
    }));

    form.append("data", JSON.stringify({ ...complaint, attachments }));
    inline_files.forEach((f) => {
      form.append("attachment", f);
    });

    const res = id
      ? await UpdateComplaint({ ...complaint, id })
      : await CreateComplaint(form);

    if (res.success) {
      if (!id) {
        setComplaint({ ...defaultCreateComplaints() });
        setFile([]);
        setUploadedVideos(new Map());
        setLookupIdentifier("");
        setLookupResult(undefined);
        setSelectedLookupDeviceId("");
        setLookupMobile("");
        setLookupAttempted(false);
      }

      showToast({
        type: ToastType.success,
        text: `Complaint ${
          id ? "Updated" : "Created"
        } successfylly complaint ID :  ${res.data.id}`,
      });

      if (!id && res.homeAppTicketCreated === true) {
        showToast({
          type: ToastType.success,
          text: "Ticket successfully created on Home App",
        });
      } else if (!id && res.homeAppTicketCreated === false) {
        showToast({
          type: ToastType.error,
          text: "Failed to create ticket on Home App",
        });
      }
    } else {
      showToast({ type: ToastType.error, text: res.error ?? "" });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (source === "service_desk_tracker") {
      setComplaint((c) => ({
        ...c,
        incident_id: incident_id,
        complainant_mobile: complainant_number ?? "",
        complainant_name: complainant_name ?? "",
        org: complainant_org ?? "",
        related_to: complaint_related_to ?? "",
        subcategory_of_related_to: subcategory_of_related_to ?? "",
      }));
      setLookupIdentifier(complainant_number ?? "");
    }
    setDisabled(
      !isAuthorised({ module: MODULE.complaint, action: [ACTION.UPDATE] }),
    );

    setComplaint((old) => {
      if (
        user &&
        user.emp_profile &&
        user.emp_profile.permissions &&
        Array.isArray(user.emp_profile.permissions) &&
        user.emp_profile.permissions.length &&
        user.emp_profile.permissions[0].org?.length
      ) {
        return {
          ...old,
          org: user?.emp_profile?.permissions[0].org,
        };
      } else {
        return old;
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedLookupDeviceId) return;

    // If this came from a phone lookup, fetch full device details using the selected device ID
    if (isValidPhoneNumber(lookupMobile)) {
      setMobileLookupLoading(true);
      GetComplaintCreationDetailsByMobile(selectedLookupDeviceId)
        .then((response) => {
          if (!response?.success) return;
          const lookup = normaliseHomePlatformLookup(response.data);
          const record = lookup.devices[0]?.raw ?? lookup.raw?.[0];
          if (!record) return;
          setLockedFields((prev) => {
            const next = new Set(prev);
            getLockedFieldsFromLookup(
              lookup,
              selectedLookupDeviceId,
              record,
            ).forEach((field) => next.add(field));
            return next;
          });
          setComplaint((c) => ({
            ...c,
            org: record.org || lookup.orgName || c.org,
            complainant_name:
              record.customerName || lookup.customerName || c.complainant_name,
            complainant_mobile:
              record.customerPhone ||
              record.customerDetails?.contact ||
              c.complainant_mobile,
            alt_mobile:
              record.customerAltPhone ||
              record.customerDetails?.altContact ||
              c.alt_mobile,
            reporter_name:
              c.reporter_name ||
              record.customerName ||
              lookup.customerName ||
              "",
            reporter_mobile:
              c.reporter_mobile ||
              record.customerPhone ||
              record.customerDetails?.contact ||
              "",
            state:
              record.state ||
              record.customerDetails?.state ||
              lookup.customerState ||
              c.state,
            city:
              record.city ||
              record.customerDetails?.city ||
              lookup.customerCity ||
              c.city,
            pincode:
              record.pincode ||
              record.customerDetails?.pincode ||
              lookup.customerPincode ||
              c.pincode,
            dealer_id: record.dealership?.id || lookup.dealerId || c.dealer_id,
          }));
        })
        .finally(() => {
          setMobileLookupLoading(false);
        });
      return;
    }

    if (!selectedRecord) return;
    setLockedFields((prev) => {
      const next = new Set(prev);
      getLockedFieldsFromLookup(
        lookupResult,
        selectedLookupDeviceId,
        selectedRecord,
      ).forEach((field) => next.add(field));
      return next;
    });
    setComplaint((c) => ({
      ...c,
      org: selectedRecord?.org || c.org,
      complainant_name: selectedRecord?.customerName || c.complainant_name,
      complainant_mobile:
        selectedRecord?.customerDetails?.contact || c.complainant_mobile,
      alt_mobile:
        selectedRecord?.customerAltPhone ||
        selectedRecord?.customerDetails?.altContact ||
        c.alt_mobile,
      reporter_name: c.reporter_name || selectedRecord?.customerName || "",
      reporter_mobile:
        c.reporter_mobile || selectedRecord?.customerDetails?.contact || "",
      state:
        selectedRecord?.state ||
        selectedRecord?.customerDetails?.state ||
        c.state,
      city:
        selectedRecord?.city || selectedRecord?.customerDetails?.city || c.city,
      pincode:
        selectedRecord?.pincode ||
        selectedRecord?.customerDetails?.pincode ||
        c.pincode,
      dealer_id: selectedRecord?.dealership?.id || c.dealer_id,
    }));
  }, [selectedLookupDeviceId]);

  return (
    <div className="p-3 sm:p-5 mb-5 text-sm">
      <div className="mx-auto w-full space-y-4 sm:space-y-6">
        {complaint.id && showComments && (
          <DrawerModal
            id="complaint_create_comment_drawer"
            show={showComments}
            title={"Comments"}
            location={DrawerModalLoaction.right}
            onClose={() => {
              setShowComments(false);
            }}
          >
            <CommentWidget complaint_id={complaint!.id} />
          </DrawerModal>
        )}
        {complaint.id && showLogs && (
          <DrawerModal
            id="complaint_create_history_drawer"
            show={showLogs}
            title={"Log history"}
            location={DrawerModalLoaction.right}
            onClose={() => {
              setShowLogs(false);
            }}
          >
            <LogsWidget complaint_id={complaint!.id} />
          </DrawerModal>
        )}
        {complaint.id && showAttachments && (
          <ModalDialog
            id="complaint_create_attachments_modal"
            show={showAttachments}
            title="Attachments"
            onClose={() => setShowAttachments(false)}
          >
            <AttachmentWidget complaint_id={complaint.id} />
          </ModalDialog>
        )}

        <div className="flex justify-center my-3">
          <h1 className="text-lg sm:text-2xl font-semibold">
            {id ? `Complaint ID : ${id}` : "Create Complaint"}
          </h1>
        </div>

        <form
          onSubmit={submit}
          autoComplete="new-password"
          className={`space-y-6 ${edit ? "pb-24" : ""}`}
        >
          <SectionCard
            title="Device identification"
            subtitle="Customer number lookup"
          >
            <div className="flex flex-row gap-2">
              <div className="flex flex-col flex-1">
                <label
                  htmlFor="complainant_number_lookup"
                  className="mr-2 text-sm leading-7"
                >
                  Registered Mobile Number / Device ID(s) <RequiredMark />
                </label>

                <input
                  disabled={!edit}
                  required
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  name="complainant_number_lookup"
                  id="complainant_number_lookup"
                  placeholder="Mobile number, a device id, or multiple device ids (comma separated)"
                  className="border px-2 py-2 h-full"
                  value={lookupIdentifier}
                  onChange={(e) => setLookupIdentifier(e.target.value)}
                  onBlur={() => {
                    if (!edit) return;

                    const raw = lookupIdentifier.trim();
                    const isPhone = isValidPhoneNumber(
                      sanitizePhoneNumber(raw),
                    );
                    const isDevice = DeviceIdRegex.test(raw.toLowerCase());
                    const tokens = raw
                      .split(/[\s,]+/)
                      .map((t) => t.trim().toLowerCase())
                      .filter(Boolean);
                    const isMultiDevice =
                      tokens.length > 1 &&
                      tokens.every((t) => DeviceIdRegex.test(t));
                    if (isPhone || isDevice || isMultiDevice)
                      fetchComplaintContextByMobile(raw);
                  }}
                />
              </div>

              <button
                id="complaint_create_fetch_details_btn"
                type="button"
                disabled={!edit || mobileLookupLoading}
                onClick={() => fetchComplaintContextByMobile()}
                className="border bg-gray-100 hover:bg-black hover:text-white rounded px-4 h-[20px] disabled:opacity-50"
              >
                {mobileLookupLoading ? "Fetching..." : "Fetch details"}
              </button>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span
                className={`rounded-full px-3 py-1 ${
                  lookupResult?.isHomePlatformComplaint &&
                  !homeAppNoInstallation
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {lookupResult?.isHomePlatformComplaint && !homeAppNoInstallation
                  ? "Home platform complaint"
                  : "Manual / ERP complaint"}
              </span>

              {lookupResult?.productName && (
                <span className="rounded-full px-3 py-1 bg-blue-50 text-blue-700">
                  Product Name: {lookupResult.productName || "--"}
                </span>
              )}
            </div>

            {homeAppNoInstallation && (
              <p className="text-sm text-red-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                No WCR / Installation is found for this device id in Home App.
                Complaint will not be created on Home App.
              </p>
            )}

            {mobileLookupLoading && (
              <div className="mt-3 text-sm text-gray-600">
                Fetching device details...
              </div>
            )}

            <div className="flex flex-col mt-4">
              <label className="mr-2 text-sm leading-7">
                Devices, working status &amp; remark
              </label>
              <DeviceTabs
                devices={deviceTokens}
                deviceMeta={complaint.device_meta ?? {}}
                editable={!!edit}
                workingStatusRequired={!isHPCL && !isHelpDeskTicket}
                showAddInput={false}
                verifyDevice={verifyDeviceForTabs}
                onChange={handleDeviceTabsChange}
              />
            </div>

            {(complaint.complaint_by ?? "").toLowerCase() === "host" &&
              (edit ? (
                <div className="flex flex-col max-w-sm mt-3">
                  <label
                    htmlFor="complaint_order_id"
                    className="mr-2 text-sm leading-7"
                  >
                    Order ID
                  </label>
                  <OrderSearchField
                    id="complaint_order_id"
                    value={complaint.order_id ?? ""}
                    onSelect={(order_id) =>
                      setComplaint((c) => ({ ...c, order_id }))
                    }
                  />
                </div>
              ) : (
                complaint.order_id && (
                  <div className="text-sm mt-3">
                    <NavLink
                      id="complaint_create_goto_order_link"
                      target="_blank"
                      to={"/orders/" + complaint.order_id}
                    >
                      <div className="mx-1 cursor-pointer flex   text-blue-400">
                        Goto : {complaint.order_id}
                      </div>
                    </NavLink>
                  </div>
                )
              ))}
          </SectionCard>

          <SectionCard
            title="Reporter / complainant"
            subtitle="Reporter name, phone, and role"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="text-sm">
                  Organization Name <RequiredMark />
                </label>
                <OrgSearchField
                  id="complaint_create_org_search"
                  key={complaint.org || "__empty__"}
                  disabled={!edit || lockedFields.has("org")}
                  placeholder="Org name"
                  value={complaint.org}
                  onSelect={(org) => {
                    setComplaint((prev) => ({
                      ...prev,
                      org: org.name,
                    }));
                  }}
                  required={true}
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="reporter_name" className="mr-2 text-sm">
                  Reporter name <RequiredMark />
                </label>
                <input
                  disabled={!edit}
                  required
                  type="text"
                  name="reporter_name"
                  id="reporter_name"
                  placeholder="Name of reporter"
                  className="border px-2 py-1"
                  value={complaint.reporter_name ?? ""}
                  onChange={(e) =>
                    setComplaint((c) => ({
                      ...c,
                      reporter_name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="reporter_phone" className="mr-2 text-sm">
                  Reporter phone <RequiredMark />
                </label>
                <input
                  disabled={!edit}
                  required
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  name="reporter_phone"
                  id="reporter_phone"
                  placeholder="Contact number"
                  className="border px-2 py-1"
                  value={complaint.reporter_mobile ?? ""}
                  onChange={(e) => handleReporterMobileChange(e.target.value)}
                  onBlur={() => {
                    if (
                      edit &&
                      isValidPhoneNumber(complaint.reporter_mobile) &&
                      !lookupResult &&
                      !lookupAttempted
                    ) {
                      fetchComplaintContextByMobile(complaint.reporter_mobile, {
                        resetFields: false,
                      });
                    }
                  }}
                />
              </div>

              <div className="flex flex-col">
                <label htmlFor="complaint_by" className="mr-2 text-sm">
                  Reporter role <RequiredMark />
                </label>
                <select
                  disabled={!edit}
                  required
                  value={complaint.complaint_by ?? ""}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      complaint_by: e.target.value,
                    }));
                  }}
                  name="complaint_by"
                  id="complaint_by"
                  className="border text-sm px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {complaint_by.map((d, i) => (
                    <option key={i} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>

              {complaint.org?.toLowerCase() !== "hpcl" && (
                <div className="flex flex-col">
                  <label className="mr-2 text-sm leading-7">
                    Pre/Post Delivery {!isHelpDeskTicket && <RequiredMark />}
                  </label>

                  <div className="border p-2 flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        id="complaint_create_pre_delivery_radio"
                        type="radio"
                        name="pre_post_delivery"
                        value="Pre Delivery"
                        disabled={!edit}
                        checked={complaint.pre_post_delivery === "Pre Delivery"}
                        onChange={(e) =>
                          setComplaint((c) => ({
                            ...c,
                            pre_post_delivery: e.target.value,
                          }))
                        }
                        required={!isHelpDeskTicket}
                      />
                      Pre
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        id="complaint_create_post_delivery_radio"
                        type="radio"
                        name="pre_post_delivery"
                        value="Post Delivery"
                        disabled={!edit}
                        checked={
                          complaint.pre_post_delivery === "Post Delivery"
                        }
                        onChange={(e) =>
                          setComplaint((c) => ({
                            ...c,
                            pre_post_delivery: e.target.value,
                          }))
                        }
                      />
                      Post
                    </label>
                  </div>
                </div>
              )}
              <div className="flex flex-col">
                <label htmlFor="res_dep" className="mr-2 text-sm leading-7">
                  Reponsible department {!isHelpDeskTicket && <RequiredMark />}
                </label>
                <select
                  disabled={!edit}
                  required={!isHelpDeskTicket}
                  value={complaint.res_dep}
                  onChange={(e) => {
                    const newDep = e.target.value;
                    setComplaint((c) => ({
                      ...c,
                      res_dep: newDep,
                      ...(newDep.includes("Home Installation") &&
                      !["incident", "service"].includes(c.ticket_type)
                        ? { ticket_type: "incident" }
                        : {}),
                    }));
                  }}
                  name="res_dep"
                  id="res_dep"
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {Object.keys(SubDepartments).map((k, i1) => (
                    <optgroup label={k} key={i1}>
                      {SubDepartments[k].map((sub_dep, i2) => (
                        <option
                          value={`${k}-${sub_dep}`}
                          key={i2}
                        >{`${sub_dep}`}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label htmlFor="res_person" className="mr-2 text-sm leading-7">
                  Reponsible person (Primary){" "}
                  {!isHelpDeskTicket && <RequiredMark />}
                </label>

                {!loading && (
                  <EmployeeSearchWidget
                    id="complaint_create_res_person_search"
                    withBtn={false}
                    onSelect={(em) => {
                      const department = em?.emp_profile?.department;
                      const subDep = em?.emp_profile?.sub_dep;

                      setComplaint((c) => ({
                        ...c,
                        res_person: em.uid!,
                        res_person_name: em.first_name + " " + em.last_name,
                        // don't overwrite res_dep for Home Installation
                        ...(!c.res_dep?.includes("Home Installation") &&
                        department &&
                        subDep
                          ? { res_dep: `${department}-${subDep}` }
                          : {}),
                      }));
                    }}
                    onClear={() => {
                      setComplaint((c) => ({
                        ...c,
                        res_person: "",
                        res_person_name: "",
                        // don't clear res_dep for Home Installation — it's set by the department dropdown, not the employee
                        ...(!c.res_dep?.includes("Home Installation")
                          ? { res_dep: "" }
                          : {}),
                      }));
                    }}
                    emp={
                      complaint.res_person
                        ? {
                            uid: complaint.res_person,
                            name: complaint.res_person_name!,
                          }
                        : undefined
                    }
                    disabled={!edit}
                    required={!isHelpDeskTicket}
                    placeholder="Primary person"
                    sub_dep={
                      isHomeInstallation
                        ? undefined
                        : complaint.res_dep?.split("-")[1]
                    }
                    department={isHomeInstallation ? "Operations" : undefined}
                  />
                )}
              </div>

              <div className="flex flex-col">
                <label
                  htmlFor="res_person_sec_dep"
                  className="mr-2 text-sm leading-7"
                >
                  Secondary person department
                </label>
                <select
                  disabled={!edit}
                  value={complaint.res_person_sec_dept || ""}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      res_person_sec_dept: e.target.value,
                      // Clear secondary person when department changes
                      res_person_sec_id: "",
                      res_person_sec_name: "",
                    }));
                  }}
                  name="res_person_sec_dep"
                  id="res_person_sec_dep"
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {Object.keys(SubDepartments).map((k, i1) => (
                    <optgroup label={k} key={i1}>
                      {SubDepartments[k].map((sub_dep, i2) => (
                        <option
                          value={`${k}-${sub_dep}`}
                          key={i2}
                        >{`${sub_dep}`}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label htmlFor="res_person" className="mr-2 text-sm leading-7">
                  Reponsible person (Secondary)
                </label>

                {!loading && (
                  <EmployeeSearchWidget
                    id="complaint_create_res_person_sec_search"
                    withBtn={false}
                    onSelect={(em) => {
                      const department = em?.emp_profile?.department;
                      const subDep = em?.emp_profile?.sub_dep;

                      setComplaint((c) => ({
                        ...c,
                        res_person_sec_id: em.uid!,
                        res_person_sec_name: em.first_name + " " + em.last_name,
                        res_person_sec_dept:
                          department && subDep ? `${department}-${subDep}` : "",
                      }));
                    }}
                    onClear={() => {
                      setComplaint((c) => ({
                        ...c,
                        res_person_sec_id: "",
                        res_person_sec_name: "",
                        res_person_sec_dept: "",
                      }));
                    }}
                    emp={
                      complaint.res_person_sec_id
                        ? {
                            uid: complaint.res_person_sec_id,
                            name: complaint.res_person_sec_name ?? "",
                          }
                        : undefined
                    }
                    disabled={!edit}
                    placeholder="Secondary person "
                    sub_dep={complaint.res_person_sec_dept?.split("-")[1]}
                  />
                )}
              </div>

              {!isHPCL && (
                <div className="flex flex-col">
                  <label htmlFor="category" className="mr-2 text-sm leading-7">
                    Category {!isHelpDeskTicket && <RequiredMark />}
                  </label>
                  <select
                    required={!isHelpDeskTicket}
                    disabled={!edit}
                    value={complaint.category ?? ""}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        category: e.target.value,
                      }));
                    }}
                    name="category"
                    id="category"
                    className="border px-2 py-1"
                  >
                    <option value="" disabled hidden>
                      Select
                    </option>
                    {[
                      "Fleets/HUB",
                      "Home Installation",
                      "Individual",
                      "RWA",
                      "Others",
                    ].map((c, i) => (
                      <option value={c} key={i}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col">
                <label htmlFor="source" className="mr-2 text-sm leading-7">
                  Source <RequiredMark />
                </label>
                <select
                  disabled={!edit}
                  value={complaint.source || ""}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      source: e.target.value,
                    }));
                  }}
                  name="source"
                  id="source"
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {Object.values(SourceType).map((c, i) => (
                    <option value={c} key={i}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Problem details"
            subtitle="Issue category, subject, and description"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
              <div className="flex flex-col">
                <label htmlFor="ticket_type" className="mr-2 text-sm leading-7">
                  Ticket Type {!isHelpDeskTicket && <RequiredMark />}
                </label>
                <select
                  required
                  disabled={!edit}
                  value={complaint.ticket_type}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      ticket_type: e.target.value,
                    }));
                  }}
                  name="ticket_type"
                  id="ticket_type"
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {isOperations ? (
                    <>
                      <option value={HomeInstallTicketType.incident}>
                        Incident
                      </option>
                      <option value={HomeInstallTicketType.service}>
                        Service Request
                      </option>
                    </>
                  ) : (
                    Object.values(ComplaintTicketType)
                      .sort()
                      .map((c, i) => (
                        <option value={c} key={i}>
                          {c}
                        </option>
                      ))
                  )}
                </select>
              </div>

              {!isHomeInstallation && (
                <div className="flex flex-col">
                  <ComplaintRelatedToSearchField
                    label="Symptom (as reported)"
                    value={complaint.related_to}
                    options={complaint_categories}
                    onChange={(value) => {
                      setComplaint((c: any) => ({
                        ...c,
                        related_to: value,
                        subcategory_of_related_to: "",
                      }));
                    }}
                    onClear={() => {
                      setComplaint((c: any) => ({
                        ...c,
                        related_to: "",
                        subcategory_of_related_to: "",
                      }));
                    }}
                    id="complaint_related_to"
                    name="related_to"
                    disabled={!edit}
                    required={true}
                    showValidationError={true}
                    customErrorMessage={
                      complaint.related_to &&
                      !complaint_categories.includes(complaint.related_to)
                        ? `Please reselect previous category: ${complaint.related_to}`
                        : undefined
                    }
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Reference only — select technical category below
                  </div>
                </div>
              )}

              {deviceTokens.length === 0 && (
                <div className="flex flex-col">
                  <label
                    htmlFor="device_working_status"
                    className="mr-2 text-sm leading-7"
                  >
                    Device working status <RequiredMark />
                  </label>
                  <select
                    disabled={!edit}
                    required
                    value={complaint.device_working_status || ""}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        device_working_status: e.target.value,
                      }))
                    }
                    name="device_working_status"
                    id="device_working_status"
                    className="border px-2 py-1"
                  >
                    <option value="" disabled hidden>
                      Select
                    </option>
                    {["Working", "Partially Working", "Not Working"].map(
                      (status) => (
                        <option value={status} key={status}>
                          {status}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              )}

              <div className="flex flex-col">
                <label htmlFor="subject" className="mr-2">
                  Subject{" "}
                  {complaint.org?.toLowerCase() !== "hpcl" && <RequiredMark />}
                </label>
                <input
                  disabled={!edit}
                  required={complaint.org?.toLowerCase() !== "hpcl"}
                  value={complaint.subject}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      subject: e.target.value,
                    }));
                  }}
                  className="border px-2 py-1 text-sm"
                  type="text"
                  name="subject"
                  id="subject"
                  placeholder="Maximum 90 characters"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="description" className="mr-2 text-sm leading-7">
                  Description
                </label>
                <textarea
                  disabled={!edit}
                  rows={4}
                  value={complaint.description}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      description: e.target.value,
                    }));
                  }}
                  className="border px-2 py-1 text-sm"
                  name="description"
                  id="description"
                  placeholder="Detailed description of issue"
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Customer" subtitle="Host details and address">
            <div className="bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end ">
                <div className="flex flex-col">
                  <label
                    htmlFor="complainant_name"
                    className="mr-2 text-sm leading-7"
                  >
                    Complainant Name{" "}
                    {complaint.org?.toLowerCase() !== "hpcl" && (
                      <RequiredMark />
                    )}
                  </label>
                  <input
                    disabled={!edit || lockedFields.has("complainant_name")}
                    required={
                      complaint?.org?.toLowerCase() === "hpcl" ||
                      !isHelpDeskTicket
                        ? false
                        : true
                    }
                    value={complaint.complainant_name}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        complainant_name: e.target.value,
                      }));
                    }}
                    className="border px-2 py-1"
                    type="text"
                    name="complainant_name"
                    id="complainant_name"
                    placeholder="name"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="complainant_number"
                    className="mr-2 text-sm leading-7"
                  >
                    Complainant Contact <RequiredMark />
                  </label>

                  <input
                    disabled={!edit || lockedFields.has("complainant_mobile")}
                    required={isHelpDeskTicket}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    name="complainant_mobile"
                    id="complainant_number"
                    placeholder="Contact number"
                    className="border px-2 py-1"
                    value={complaint.complainant_mobile ?? ""}
                    title="Please enter a valid 10 digit mobile number"
                    onChange={(e) => {
                      const value = sanitizePhoneNumber(e.target.value);
                      setComplaint((c) => ({
                        ...c,
                        complainant_mobile: value,
                      }));
                    }}
                    onBlur={() => {
                      if (
                        edit &&
                        isValidPhoneNumber(complaint.complainant_mobile) &&
                        !lookupResult &&
                        !lookupAttempted
                      ) {
                        fetchComplaintContextByMobile(
                          complaint.complainant_mobile,
                          { resetFields: false },
                        );
                      }
                    }}
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="alt_number"
                    className="mr-2 text-sm leading-7"
                  >
                    Alternative Contact
                  </label>
                  <input
                    disabled={!edit}
                    value={complaint.alt_mobile}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        alt_mobile: sanitizePhoneNumber(e.target.value),
                      }));
                    }}
                    className="border px-2 py-1"
                    type="tel"
                    inputMode="numeric"
                    title="Please enter a valid 10 digit mobile number"
                    name="alt_mobile"
                    id="alt_number"
                    placeholder="Alternative contact number"
                  />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="dealer_id" className="mr-2 text-sm leading-7">
                    Dealer ID
                  </label>
                  <input
                    disabled={!edit || lockedFields.has("dealer_id")}
                    value={complaint.dealer_id}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        dealer_id: e.target.value,
                      }));
                    }}
                    className="border px-2 py-1"
                    type="text"
                    name="dealer_id"
                    id="dealer_id"
                    placeholder="Dealer ID"
                  />
                </div>
                <div className="flex flex-col col-span-2">
                  <label htmlFor="address-picker">Address</label>
                  <div
                    id="complaint_create_address_picker"
                    onClick={() => {
                      if (!edit || lockedFields.has("address")) return;
                      showModal({
                        type: ModalType.drawer,
                        title: "Address Picker",
                        alignment: ModalAlignment.right,
                        container(id) {
                          return (
                            <div className="  bg-gray-100 p-1 h-full w-full">
                              <AddressPickerWidget
                                data={complaint.address}
                                location_required={false}
                                onSubmit={function (
                                  _address: AddressModel,
                                ): void {
                                  setComplaint((o) => ({
                                    ...o!,
                                    address: { ...o!.address, ..._address },
                                  }));
                                  onClose(id);
                                }}
                                onCancel={function (): void {
                                  onClose(id);
                                }}
                              />
                            </div>
                          );
                        },
                      });
                    }}
                    className={`border rounded px-2 p-1 focus:outline-none h-32 ${
                      edit && !lockedFields.has("address")
                        ? ""
                        : "opacity-60 cursor-not-allowed"
                    }`}
                  >
                    {(complaint.address && complaint.address.full_address) ??
                      "Full address"}
                  </div>
                </div>
                <div className="flex flex-col">
                  <label htmlFor="state" className="mr-2 text-sm leading-7">
                    State
                  </label>
                  <input
                    disabled={!edit || lockedFields.has("state")}
                    value={complaint.state}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        state: e.target.value,
                      }));
                    }}
                    className="border px-2 py-1"
                    type="text"
                    name="state"
                    id="state"
                    placeholder="State"
                    required={!isHelpDeskTicket}
                  />
                </div>
                <div className="flex flex-col w-full">
                  <label htmlFor="pincode" className="mr-2 text-sm leading-7">
                    Postal code
                  </label>
                  <input
                    disabled={!edit || lockedFields.has("pincode")}
                    type="number"
                    name="pincode"
                    placeholder="Postal code"
                    value={complaint.pincode}
                    onChange={(e) => {
                      const t = e.target.value;
                      if (e.target.value.length <= 6)
                        setComplaint((o) => ({ ...o!, pincode: t }));
                    }}
                    className="border px-2 py-1"
                    id="pincode"
                  />
                </div>
                {source === "service_desk_tracker" && (
                  <div className="flex flex-col w-full">
                    <label
                      htmlFor="incident_id"
                      className="mr-2 text-sm leading-7"
                    >
                      Service Desk ID <RequiredMark />
                    </label>
                    <input
                      disabled
                      type="text"
                      name="incident_id"
                      placeholder="Service Desk ID"
                      value={complaint.incident_id}
                      className="border px-2 py-1"
                      id="incident_id"
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Ticket details" subtitle="">
            <div className="space-y-6">
              {/* ROW 1 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Visibility */}
                <div className="flex flex-col gap-1">
                  <label htmlFor="active" className="text-sm text-gray-600">
                    Visibility
                  </label>
                  <select
                    className="border rounded px-3 py-2 text-sm w-full focus:outline-none"
                    name="active"
                    id="active"
                    disabled={!edit}
                    value={complaint.active ? "true" : "false"}
                    onChange={(e) =>
                      setComplaint((o) => ({
                        ...o,
                        active: e.target.value === "true",
                      }))
                    }
                  >
                    <option value="" hidden>
                      Select
                    </option>
                    <option value="true">Active</option>
                    <option value="false">Closed</option>
                  </select>
                </div>

                {/* Status */}
                <div className="flex flex-col gap-1">
                  <label htmlFor="status" className="text-sm text-gray-600">
                    Status <RequiredMark />
                  </label>
                  <select
                    disabled={!edit}
                    required
                    value={complaint.status}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        status: e.target.value,
                      }))
                    }
                    className="border rounded px-3 py-2 text-sm w-full bg-yellow-50 focus:outline-none"
                  >
                    <option value="" disabled hidden>
                      Select
                    </option>
                    {Object.values(homeInstallationComplaintStatus).map(
                      (d, i) => (
                        <option value={d} key={i}>
                          {d}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                {/* Complaint Date */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="complaint_date"
                    className="text-sm text-gray-600"
                  >
                    Date <RequiredMark />
                  </label>
                  <input
                    disabled={!edit}
                    required
                    type="date"
                    id="complaint_date"
                    value={moment(complaint.complaint_date).format(
                      "YYYY-MM-DD",
                    )}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        complaint_date:
                          new Date(e.target.value).getTime() -
                          1000 * 60 * 60 * 5.5,
                      }))
                    }
                    className="border rounded px-3 py-2 text-sm w-full focus:outline-none"
                  />
                </div>

                {/* Due Date */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="next_action_date"
                    className="text-sm text-gray-600"
                  >
                    Due Date <RequiredMark />
                  </label>
                  <input
                    disabled={!edit}
                    required
                    type="date"
                    min={moment().format("YYYY-MM-DD")}
                    id="next_action_date"
                    value={moment(complaint.next_action_date).format(
                      "YYYY-MM-DD",
                    )}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        next_action_date:
                          new Date(e.target.value).getTime() -
                          1000 * 60 * 60 * 5.5,
                      }))
                    }
                    className="border rounded px-3 py-2 text-sm w-full focus:outline-none"
                  />
                </div>
              </div>

              {/* ROW 2 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Priority */}
                <div className="flex flex-col gap-1">
                  <label htmlFor="priority" className="text-sm text-gray-600">
                    Priority
                  </label>
                  <select
                    disabled={!edit}
                    required
                    value={complaint.priority}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        priority: e.target.value,
                      }))
                    }
                    className="border rounded px-3 py-2 text-sm w-full focus:outline-none"
                  >
                    <option value="" disabled hidden>
                      Select
                    </option>
                    {Object.values(PriorityType).map((d, i) => (
                      <option value={d} key={i}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Internal classification"
            subtitle="Hidden from customers and dealers. Full ERP technical taxonomy."
          >
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col">
                  {isHomeInstallation ? (
                    <>
                      <label
                        htmlFor="related_to_hi"
                        className="mr-2 text-sm leading-7"
                      >
                        Complaint category <RequiredMark />
                      </label>
                      <select
                        id="related_to_hi"
                        name="related_to"
                        disabled={!edit}
                        value={complaint.related_to || ""}
                        onChange={(e) => {
                          setComplaint((c: any) => ({
                            ...c,
                            related_to: e.target.value,
                            category: "",
                            subcategory_of_related_to: "",
                          }));
                        }}
                        className="border px-2 py-1"
                      >
                        <option value="" disabled hidden>
                          Select
                        </option>
                        {complaint_categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <ComplaintRelatedToSearchField
                      label="Complaint category"
                      value={complaint.related_to}
                      options={complaint_categories}
                      onChange={(value) => {
                        setComplaint((c: any) => ({
                          ...c,
                          related_to: value,
                          subcategory_of_related_to: "",
                        }));
                      }}
                      onClear={() => {
                        setComplaint((c: any) => ({
                          ...c,
                          related_to: "",
                          subcategory_of_related_to: "",
                        }));
                      }}
                      id="complaint_related_to"
                      name="related_to"
                      disabled={!edit}
                      required={true}
                      showValidationError={true}
                      customErrorMessage={
                        complaint.related_to &&
                        !complaint_categories.includes(complaint.related_to)
                          ? `Please reselect previous category: ${complaint.related_to}`
                          : undefined
                      }
                    />
                  )}
                </div>

                <div className="flex flex-col">
                  {isHomeInstallation ? (
                    <>
                      <label
                        htmlFor="subcategory_hi"
                        className="mr-2 text-sm leading-7"
                      >
                        Complaint subcategory
                      </label>
                      <select
                        id="subcategory_hi"
                        name="subcategory_of_related_to"
                        disabled={!edit || subCategories.length === 0}
                        value={complaint.subcategory_of_related_to || ""}
                        onChange={(e) =>
                          setComplaint((c: any) => ({
                            ...c,
                            subcategory_of_related_to: e.target.value,
                            category: e.target.value,
                          }))
                        }
                        className="border px-2 py-1"
                      >
                        <option value="" disabled hidden>
                          Select
                        </option>
                        {subCategories.map((sub) => (
                          <option key={sub} value={sub}>
                            {sub}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <ComplaintRelatedToSearchField
                      label="Complaint subcategory"
                      value={complaint.subcategory_of_related_to || ""}
                      options={subCategories}
                      onChange={(value) => {
                        setComplaint((c: any) => ({
                          ...c,
                          subcategory_of_related_to: value,
                        }));
                      }}
                      id="subcategory_of_related_to"
                      name="subcategory_of_related_to"
                      disabled={!edit || subCategories.length === 0}
                      required={isSubCategoryRequired}
                      showValidationError={true}
                      customErrorMessage={
                        isSubCategoryRequired &&
                        complaint.subcategory_of_related_to &&
                        !subCategories.includes(
                          complaint.subcategory_of_related_to,
                        )
                          ? `Please reselect previous sub-category: ${complaint.subcategory_of_related_to}`
                          : undefined
                      }
                    />
                  )}
                </div>

                <div className="flex flex-col">
                  <label htmlFor="severity" className="mr-2 text-sm leading-7">
                    Severity
                  </label>
                  <select
                    disabled={!edit}
                    value={complaint.severity || PriorityType.medium}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        severity: e.target.value,
                      }));
                    }}
                    name="severity"
                    id="severity"
                    className="border px-2 py-1"
                  >
                    {Object.values(PriorityType).map((val, i) => (
                      <option value={val} key={i}>
                        {val}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    id="complaint_create_safety_flag_checkbox"
                    type="checkbox"
                    checked={!!complaint.safety_flag}
                    disabled={!edit}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        safety_flag: e.target.checked,
                      }))
                    }
                  />
                  Safety risk flag
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    id="complaint_create_rca_required_checkbox"
                    type="checkbox"
                    checked={!!complaint.rca_required}
                    disabled={!edit}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        rca_required: e.target.checked,
                      }))
                    }
                  />
                  RCA required
                </label>
              </div>
            </div>
          </SectionCard>
          {complaint.id && (
            <div className="flex  my-5  gap-3">
              <button
                id="complaint_create_comment_btn"
                type="button"
                onClick={() => setShowComments(true)}
                className="border rounded px-2 py-1"
              >
                Comment
              </button>
              <button
                id="complaint_create_history_btn"
                type="button"
                onClick={() => setShowLogs(true)}
                className="border rounded px-2 py-1"
              >
                History
              </button>
              <button
                id="complaint_create_attachments_btn"
                type="button"
                onClick={() => setShowAttachments(true)}
                className={
                  " rounded border px-3 py-1 hover:bg-black hover:text-white  "
                }
              >
                Attachments
              </button>
              {!disabled && (
                <button
                  id="complaint_create_edit_btn"
                  type="button"
                  onClick={() => setEdit(true)}
                  className={
                    " rounded border px-3 py-1 hover:bg-black hover:text-white  " +
                    (edit ? "hidden" : "block")
                  }
                >
                  Edit
                </button>
              )}
            </div>
          )}
          {!complaint.id && (
            <div className="my-5">
              <p className=" text-sm">Attachments</p>
              <div className="grid grid-cols-3 mb-10">
                <div id="complaint_create_attachment_file_drop_zone">
                  <FileDropZone
                    onChange={async (selected) => {
                      if (!selected.length) return;

                      try {
                        const temp: File[] = [];

                        for (const file of selected) {
                          const category = getAttachmentCategory(file);

                          if (category === "image") {
                            temp.push(await compressImage(file));
                            continue;
                          }

                          if (category !== "video") {
                            temp.push(file);
                            continue;
                          }

                          const prepared = await prepareVideo(file);
                          if (!prepared) continue;

                          // Only keep the video in the list once it is safely
                          // in S3, so submit can never send an unresolved one.
                          const url = await uploadVideo(prepared);
                          if (url) temp.push(prepared);
                        }

                        if (temp.length) setFile((prev) => [...prev, ...temp]);
                      } catch (error: any) {
                        showToast({
                          type: ToastType.error,
                          text:
                            error?.message ??
                            "Could not process the selected file. Please try again.",
                        });
                      }
                    }}
                    type={COMPLAINT_ATTACHMENT_ACCEPT}
                    multiSelect={true}
                    limits={COMPLAINT_ATTACHMENT_LIMITS}
                    existingFiles={files}
                    maxTotalSizeMB={COMPLAINT_ATTACHMENT_TOTAL_MB}
                    excludeFromTotal={["video"]}
                  />
                </div>
                {files.length > 0 && (
                  <div className="flex flex-wrap col-span-2">
                    {files.map((file, i) => (
                      <div className="" key={i}>
                        {getAttachmentCategory(file) === "image" ? (
                          <ImageTile url={URL.createObjectURL(file)} />
                        ) : getAttachmentCategory(file) === "video" ? (
                          <video
                            src={URL.createObjectURL(file)}
                            className="h-24 w-24 object-cover rounded bg-black"
                            muted
                            playsInline
                            controls
                          />
                        ) : (
                          <PdfTile url={URL.createObjectURL(file)} />
                        )}
                        <div className="text-xs text-gray-500 max-w-24 truncate">
                          {formatSizeMB(file.size)}
                        </div>
                        <div className="flex justify-end">
                          <p
                            id={`complaint_create_attachment_file_${i}_delete_btn`}
                            className="cursor-pointer text-blue-400"
                            onClick={() => {
                              const [removed] = files.splice(i, 1);
                              setFile([...files]);
                              setUploadedVideos((prev) => {
                                const next = new Map(prev);
                                next.delete(removed);
                                return next;
                              });
                            }}
                          >
                            delete
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {videoCompression && (
                <div className="my-3 max-w-md border rounded p-3 bg-gray-50">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="truncate max-w-xs">
                      {videoCompression.stage === "loading"
                        ? "Preparing video compressor (one-time 31 MB download)…"
                        : `Compressing ${videoCompression.name}…`}
                    </span>
                    <span>{videoCompression.percent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 my-2">
                    <div
                      className="bg-green-600 h-1.5 rounded-full"
                      style={{
                        width: `${
                          videoCompression.percent < 2
                            ? 2
                            : videoCompression.percent
                        }%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-500">
                      You can keep filling the form. Large videos may take a few
                      minutes.
                    </p>
                    <button
                      id="complaint_create_video_compression_cancel_btn"
                      type="button"
                      className="text-xs border rounded-full px-2 py-1"
                      onClick={videoCompression.cancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {Object.keys(videoProgress).length > 0 && (
                <div className="my-3 max-w-md">
                  <p className="text-xs font-semibold text-gray-500 mb-1">
                    Uploading videos — please keep this page open
                  </p>
                  {Object.entries(videoProgress).map(([name, percent]) => (
                    <div key={name} className="mb-2">
                      <div className="flex justify-between text-xs">
                        <span className="truncate max-w-xs">{name}</span>
                        <span>{percent}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full"
                          style={{ width: `${percent < 2 ? 2 : percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {complaint.id && (
            <p className="flex gap-5 text-sm my-3">
              <span>
                {" "}
                Created by :<b>{complaint.created_by_name ?? "--"}</b>
                <span className="text-xs mx-2">
                  {moment(complaint.created_at, "YYYY-MM-DD hh:mm:ss").format(
                    "D MMM YYYY HH:MM a",
                  )}
                </span>
              </span>
              <span>
                {" "}
                Updated by :<b>{complaint.updated_by_name ?? "--"}</b>
                <span className="text-xs mx-2">
                  {moment(complaint.updated_at, "YYYY-MM-DD hh:mm:ss").format(
                    "D MMM YYYY HH:MM a",
                  )}
                </span>
              </span>
            </p>
          )}

          {edit && (
            <div
              className="flex justify-center gap-3 sm:gap-5 fixed bottom-0 left-0 lg:left-60 right-0 z-40 bg-white p-3
                border-t border-gray-200
                shadow-[0_-4px_10px_rgba(0,0,0,0.12)]"
            >
              {complaint.id && (
                <button
                  id="complaint_create_cancel_edit_btn"
                  onClick={() => {
                    setEdit(false);
                    // setComplaint(preVal);
                  }}
                  type="button"
                  className="border bg-gray-100  flex gap-2 hover:bg-black hover:text-white rounded items-center px-3 py-1"
                >
                  Cancel
                </button>
              )}
              <button
                id="complaint_create_submit_btn"
                className="border bg-gray-100  flex gap-2 hover:bg-black hover:text-white rounded items-center px-3 py-1"
              >
                {id ? "Update" : "Create"}
              </button>
            </div>
          )}
        </form>
        <hr />

        <LoadingWidget loading={loading} />
      </div>
    </div>
  );
};

export default CreateComplaintPage;
